import { distinctUntilChanged } from "rxjs/operators";
import React from "react";
import { BehaviorSubject } from "rxjs";
import { Group } from "@iventis/types";
import { IGridColours } from "@iventis/styles";
import { defaultIGridColours } from "@iventis/styles/src/constants";
import { RowHeaderLayout } from "../interactive-grid.types";

/**
 * Interactive Grid Context Implementation
 * Singleton resource used throughout grid components as a central point for state sharing
 */
export class IGridContextImplementation {
    public xTranslation = new BehaviorSubject<number>(0);

    public yTranslation = new BehaviorSubject<number>(0);

    public translation = new BehaviorSubject<[number, number]>([0, 0]);

    public pannableRange = new BehaviorSubject<[number, number]>([0, 0]);

    public panning = new BehaviorSubject<boolean>(false);

    public rowHeaderGroups = new BehaviorSubject<Group[]>([]);

    private rowHeaderLayout = new BehaviorSubject<RowHeaderLayout[]>([]);

    private contentSize: [number, number] = [0, 0];

    private clipZoneSize: [number, number] = [0, 0];

    public colours = new BehaviorSubject<IGridColours>(defaultIGridColours);

    /**
     * Publish the row layouts. This is required because we do not know the pixel
     * sizes of these rows until after they have been rendered. This is then used by
     * the row headers to match their heights.
     * @param layout The layout to publish
     */
    public setRowHeaderLayout(layout: RowHeaderLayout[]) {
        this.rowHeaderLayout.next(layout);
    }

    /**
     * Get row header layouts with distinction optimisation
     * @returns An observable containing row header layouts. Only emits when the number of row headers change.
     */
    public getRowHeaderLayoutDistinct() {
        return this.rowHeaderLayout.pipe(
            distinctUntilChanged((previous, current) => {
                if (previous.length !== current.length) {
                    return false;
                }
                // If the heights of any of the rows are different, then the layout has changed.
                // Bear in mind that this also accounts for children, since the parent height includes the children heights
                return !previous.some((previousEntry, index) => previousEntry.height !== current[index].height);
            })
        );
    }

    public updateTranslation(x: number, y: number) {
        this.xTranslation.next(x);
        this.yTranslation.next(y);
        this.translation.next([x, y]);
    }

    public setRowHeaderGroups(groups: Group[]) {
        this.rowHeaderGroups.next(groups);
    }

    public updateContentSize(width: number, height: number) {
        this.contentSize = [width, height];
        this.updatePannableRange();
    }

    public updateClipZoneSize(width: number, height: number) {
        this.clipZoneSize = [width, height];
        this.updatePannableRange();

        // When the clip zone size changes, our translation may be out of bounds. Therefore we need to reset the translation.
        const [xRange, yRange] = this.pannableRange.value;
        const xTranslation = xRange === 0 ? 0 : this.translation.value[0];
        const yTranslation = yRange === 0 ? 0 : this.translation.value[1];

        this.updateTranslation(xTranslation, yTranslation);
    }

    public setPanning(panning: boolean) {
        this.panning.next(panning);
    }

    private updatePannableRange() {
        const xRange = Math.max(this.contentSize[0] - this.clipZoneSize[0], 0);
        const yRange = Math.max(this.contentSize[1] - this.clipZoneSize[1], 0);
        this.pannableRange.next([Math.abs(xRange), Math.abs(yRange)]);
    }
}

export const IGridContext = React.createContext<IGridContextImplementation>(undefined);
